import { formatQuantity, formatWireMoney } from './money';
import { SYNTHETIC_RATE_PREFIX, payBasisLabels, type ServiceRate } from './types';

export type RateStatus = 'In effect' | 'Scheduled' | 'Ended';

export const todayIso = (): string => new Date().toISOString().slice(0, 10);

/** The status as a WORD (never colour alone): a row that ended, one that has not started, or one in effect. `effectiveTo` is the first day it no longer applies. */
export function rateStatus(rate: Pick<ServiceRate, 'effectiveFrom' | 'effectiveTo'>, today: string = todayIso()): RateStatus {
  if (rate.effectiveTo !== null && (rate.effectiveTo <= today || rate.effectiveTo <= rate.effectiveFrom)) return 'Ended';
  if (rate.effectiveFrom > today) return 'Scheduled';
  return 'In effect';
}

export function differentialText(rate: Pick<ServiceRate, 'differentialKind' | 'differentialValue'>): string {
  if (rate.differentialKind === 'Amount' && rate.differentialValue !== null) return `+ ${formatWireMoney(rate.differentialValue)}`;
  if (rate.differentialKind === 'Percent' && rate.differentialValue !== null) return `+ ${formatQuantity(rate.differentialValue)}%`;
  return 'None';
}

export const isSyntheticRate = (rate: Pick<ServiceRate, 'description'>): boolean => rate.description.startsWith(SYNTHETIC_RATE_PREFIX);

/** The worker category of a rate row in words: its name, or "Any worker category" for a company rate that names none. */
export function rateCategoryLabel(rate: Pick<ServiceRate, 'disciplineName' | 'appliesToAnyWorkerCategory'>, fallback = 'A worker category'): string {
  return rate.appliesToAnyWorkerCategory ? 'Any worker category' : (rate.disciplineName ?? fallback);
}

/** One line naming a rate row, for a conflict message: "Registered Nurse / Start of Care, Per Visit, from 2025-01-01 (still in effect)". */
export function describeRateRow(rate: ServiceRate): string {
  const scope = `${rateCategoryLabel(rate)} / ${rate.serviceTypeName ?? 'any work type'}`;
  const span = rate.effectiveTo ? `from ${rate.effectiveFrom} to ${rate.effectiveTo}` : `from ${rate.effectiveFrom}, still in effect`;
  return `${scope}, ${payBasisLabels[rate.payBasis]}, ${span}`;
}
